<script setup>
import { computed, ref } from "vue";
import { useTopicStore } from "@/stores/useTopicStore";
import DeleteConfirmModal from "@/components/Generics/DeleteConfirmModal.vue";

const emit = defineEmits(["close", "refresh", "notify"]);

const topicStore = useTopicStore();

// Quantidade "viva" (store pode limpar IDs durante o delete)
const selectedCount = computed(() => topicStore.selectedTopicIds.length);

// Congela a quantidade no momento que o modal abre
const frozenCount = ref(selectedCount.value);

if (!frozenCount.value || frozenCount.value < 1) frozenCount.value = 1;

// ===============================
// Tratamento de erro específico
// ===============================
function buildBatchTopicDeleteError(error) {
  const FOREIGN_KEY_ERROR_STRING =
    "Erro interno ao remover assuntos: Cannot delete or update a parent row: a foreign key constraint fails (`litterae`.`texts`, CONSTRAINT `fk_texts_topics` FOREIGN KEY (`topic_id`) REFERENCES `topics` (`topic_id`))";

  const raw =
    error?.response?.data?.message ||
    error?.message ||
    "";

  let userFriendlyMessage =
    "Ocorreu um erro ao tentar excluir os assuntos em lote.";

  const looksLikeFK =
    raw.includes("foreign key constraint fails") ||
    raw.toLowerCase().includes("constraint") ||
    raw.toLowerCase().includes("foreign key");

  if (raw.trim() === FOREIGN_KEY_ERROR_STRING.trim() || looksLikeFK) {
    userFriendlyMessage =
      "Um ou mais assuntos selecionados estão vinculados a Textos e não podem ser removidos.\nPara excluí-los, remova todos os textos associados a estes assuntos primeiro.";
  } else if (raw) {
    userFriendlyMessage = raw;
  }

  return userFriendlyMessage;
}

const messageTitle = computed(() => {
  if (frozenCount.value === 1) {
    return "Tem certeza que deseja excluir este assunto?";
  }

  return `Tem certeza que deseja excluir ${frozenCount.value} assuntos?`;
});

const messageSubtitle = computed(() => {
  return "Esta ação não pode ser desfeita.";
});

const successMessage = computed(() => {
  if (frozenCount.value === 1) {
    return "Assunto apagado com sucesso.";
  }

  return `${frozenCount.value} assuntos apagados com sucesso.`;
});

async function onConfirmBatchDelete() {
  try {
    await topicStore.deleteSelectedTopics();

    topicStore.shouldForcePageOne = true;
    topicStore.selectedTopicIds = [];

    emit("notify", {
      type: "success",
      message: successMessage.value,
    });

    emit("refresh");
    emit("close");
  } catch (error) {
    const msg = buildBatchTopicDeleteError(error);

    emit("notify", {
      type: "error",
      message: msg,
    });

    throw new Error(msg);
  }
}
</script>

<template>
  <DeleteConfirmModal
    :show="true"
    title="Apagar Assuntos em Lote"
    :messageTitle="messageTitle"
    :messageSubtitle="messageSubtitle"
    confirmText="Sim, apagar"
    cancelText="Cancelar"
    loadingText="Apagando..."
    :onConfirm="onConfirmBatchDelete"
    @close="emit('close')"
  />
</template>
